import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { DATE_FORMATS_FNS } from "components";
import { formatDate } from "utils/dateHelperFunctions";
import { formatPrice } from "utils/formatPrice";

import type { AccessorColumnDef } from "@tanstack/react-table";
import type { SlaAgreement, SlaRateValue, SlaRentalClass } from "components/ROB/queryKeys";

export type RateRow = SlaRateValue & {
  sla: string;
  startDate: string;
  preferredMakes?: string;
};

export type RentalClassRow = SlaRentalClass & {
  sla: string;
  startDate: string;
};

export const useRateColumns = () => {
  const { t } = useTranslation();

  return useMemo<AccessorColumnDef<RateRow>[]>(
    () => [
      { id: "robCode", size: 90, accessorKey: "robCode", header: () => t("v8_rob_code", "ROB code"), cell: ({ row: { original } }) => original.robCode },
      {
        id: "rob_code_description",
        size: 320,
        accessorKey: "rob_code_description",
        header: () => t("v8_description", "Description"),
        cell: ({ row: { original } }) => original.rob_code_description
      },
      {
        id: "operationCode",
        size: 90,
        accessorKey: "operationCode",
        header: () => t("v8_operation_code", "Operation code"),
        cell: ({ row: { original } }) => original.operationCode
      },
      {
        id: "operation_code_description",
        accessorKey: "operation_code_description",
        header: () => t("v8_operation", "Operation"),
        cell: ({ row: { original } }) => original.operation_code_description
      },
      {
        id: "objectTypeCategory",
        size: 80,
        accessorKey: "objectTypeCategory",
        header: () => t("v8_category", "Category"),
        cell: ({ row: { original } }) => original.objectTypeCategory
      },
      {
        id: "preferredMakes",
        accessorKey: "preferredMakes",
        header: () => t("v8_preferred_makes", "Preferred makes"),
        cell: ({ row: { original } }) => original.preferredMakes
      },
      { id: "sla", accessorKey: "sla", header: () => "SLA", cell: ({ row: { original } }) => original.sla },
      {
        id: "startDate",
        accessorKey: "startDate",
        header: () => t("v8_start_date", "Start date"),
        cell: ({ row: { original } }) => formatDate(original.startDate, DATE_FORMATS_FNS.dateMonthYear)
      },
      { id: "price", accessorKey: "price", header: () => t("v8_price", "Price"), cell: ({ row: { original } }) => `€ ${formatPrice(original.price)}` }
    ],
    [t]
  );
};

export const useRentalClassColumns = () => {
  const { t } = useTranslation();

  return useMemo<AccessorColumnDef<RentalClassRow>[]>(
    () => [
      {
        id: "rentalClass",
        size: 90,
        accessorKey: "rentalClass",
        header: () => t("v8_rental_class", "Rental class"),
        cell: ({ row: { original } }) => original.rentalClass
      },
      { id: "fuelType", accessorKey: "fuelType", header: () => t("v8_fuel_type", "Fuel type"), cell: ({ row: { original } }) => original.fuelType },
      {
        id: "forEstate",
        accessorKey: "forEstate",
        header: () => t("v8_estate", "Estate"),
        cell: ({ row: { original } }) => (original.forEstate ? t("v8_yes", "Yes") : t("v8_no", "No"))
      },
      {
        id: "dailyRate",
        accessorKey: "dailyRate",
        header: () => t("v8_daily_rate", "Daily rate"),
        cell: ({ row: { original } }) => `€ ${formatPrice(original.dailyRate)}`
      },
      {
        id: "freeKilometrage",
        accessorKey: "freeKilometrage",
        header: () => t("v8_free_kilometrage", "Free kilometrage"),
        cell: ({ row: { original } }) => original.freeKilometrage
      },
      {
        id: "kilometrageSurchargeRate",
        accessorKey: "kilometrageSurchargeRate",
        header: () => t("v8_kilometrage_surcharge", "Kilometrage surcharge"),
        cell: ({ row: { original } }) => `€ ${formatPrice(original.kilometrageSurchargeRate)}`
      },
      {
        id: "automaticTransmissionSurchargeRate",
        size: 220,
        accessorKey: "automaticTransmissionSurchargeRate",
        header: () => t("v8_automatic_transmission_surcharge", "Automatic transmission surcharge"),
        cell: ({ row: { original } }) => `€ ${formatPrice(original.automaticTransmissionSurchargeRate)}`
      },
      { id: "sla", accessorKey: "sla", header: () => "SLA", cell: ({ row: { original } }) => original.sla },
      {
        id: "startDate",
        accessorKey: "startDate",
        header: () => t("v8_start_date", "Start date"),
        cell: ({ row: { original } }) => formatDate(original.startDate, DATE_FORMATS_FNS.dateMonthYear)
      }
    ],
    [t]
  );
};

export const useAgreementColumns = () => {
  const { t } = useTranslation();

  return useMemo<AccessorColumnDef<SlaAgreement>[]>(
    () => [
      { id: "name", accessorKey: "name", header: () => t("v8_name", "Name"), cell: ({ row: { original } }) => original.name },
      { id: "status", accessorKey: "status", header: () => t("v8_status", "Status"), cell: ({ row: { original } }) => original.status },
      {
        id: "isClusterAgreement",
        accessorKey: "isClusterAgreement",
        header: () => t("v8_is_cluster_agreement", "Is cluster agreement"),
        cell: ({ row: { original } }) => (original.isClusterAgreement ? t("v8_yes", "Yes") : t("v8_no", "No"))
      },
      {
        id: "lessors",
        accessorKey: "lessors",
        header: () => t("v8_lessors", "Lessors"),
        cell: ({ row: { original } }) => original.lessors.map(lessor => lessor.name).join(", ")
      }
    ],
    [t]
  );
};
